import styled from 'styled-components';
import { differenceInDays, parseISO } from 'date-fns';
import {
  Text2,
  Text3,
  Text3B,
  Text4,
  Text4B,
  Text5B,
} from '../genericComponents/texts';
import { BlankSpace } from '../genericComponents/layouts';
import Publications from '../models/publications.model';
import { priceFormat, roundPrice } from '../priceFormat';
import RankingChileautos from '../models/rankingChileautos.model';
import infoImg from '../assets/img/info.svg';
import chileautosLogo from '../assets/img/chileautos.svg';
import bellImg from '../assets/img/bell.svg';
import { b2bBaseUrl } from '../config/config';
import autoFelizImg from '../assets/img/autoFeliz.svg';
import PublicationsList from '../models/publicationsList.model';
import { getInvalidReasonLabel, getPortalNameFromId } from '../utils/publicationPortal';

const DISCARDED_TAB = 'Publicaciones Descartadas';

function mapInvalidsToPublicationRows(
  invalids: NonNullable<Publications['invalids']>,
): PublicationsList[] {
  return invalids.map((inv) => ({
    portal: getPortalNameFromId(inv.portal_id),
    precio: inv.precio,
    url_respaldo: inv.url_respaldo,
    ss_path: inv.ss_path,
    fecha_est: inv.fecha_est,
    seller_type: inv.seller_type,
    motivo_inv: inv.motivo_inv,
  }));
}

function publicationsTableSubtitle(portal: string): string {
  if (portal === 'Otras publicaciones') return 'en otros portales';
  if (portal === DISCARDED_TAB) return 'descartadas';
  return `en ${portal}`;
}

interface Props {
  publications: Publications;
  createdAt: string | undefined;
  rankingChileautos: RankingChileautos;
  haveSimilarVersions: boolean;
  haveYearPrices: boolean;
}

export default function PublicationsPDF({
  publications, createdAt, rankingChileautos, haveSimilarVersions, haveYearPrices,
}: Readonly<Props>) {
  const portals = [
    'Chileautos',
    'Facebook',
    'Mercado Libre',
    'Yapo',
    'Otras publicaciones',
    DISCARDED_TAB,
  ];

  // Asegurar que publications.list siempre sea un array
  const publicationList = publications.list || [];

  const averagePublications = publications.price_average ?? 0;
  const countPublications = publications.list?.length ?? 0;

  const statusDescription = {
    'MUY ALTA': 'Más de 50 publicaciones activas',
    ALTA: 'Más de 11 publicaciones activas',
    MODERADA: 'Menos de 11 publicaciones activas',
    BAJA: 'Menos de 3 publicaciones activas',
    'MUY BAJA': 'Menos de 3 publicaciones activas',
  };

  // Filtrar publicaciones por portal (descartadas solo desde `invalids`)
  const getPublicationsByPortal = (portal: string) => {
    if (portal === DISCARDED_TAB) {
      return mapInvalidsToPublicationRows(publications.invalids ?? []);
    }
    if (portal === 'Otras publicaciones') {
      return publicationList.filter((pub) => (
        pub.portal !== 'Chileautos'
        && pub.portal !== 'Facebook'
        && pub.portal !== 'Mercado Libre'
        && pub.portal !== 'Yapo'
      ));
    }
    return publicationList.filter((pub) => pub.portal === portal);
  };

  const chileautosPublications = publications.list?.filter((pub) => pub.portal === 'Chileautos') || [];

  const averagePriceChileautos = chileautosPublications.reduce((acc, pub) => acc + pub.precio, 0) / chileautosPublications.length;

  // Calcular el tiempo promedio de publicación de Chileautos
  let averageDurationChileautos = Math.round(
    chileautosPublications.reduce((acc, pub) => {
      if (pub.fecha_est && createdAt) {
        try {
          const fechaEst = parseISO(pub.fecha_est);
          const created = parseISO(createdAt);
          return acc + Math.abs(differenceInDays(created, fechaEst));
        } catch {
          return acc;
        }
      }
      return acc;
    }, 0) / chileautosPublications.length,
  );

  if (publications.days_until_sale) {
    averageDurationChileautos = publications.days_until_sale;
  }

  // Calcular la velocidad promedio de venta de Chileautos
  let averageSpeedChileautos = '-';
  let textAverageSpeedChileautos = '';
  if (!Number.isNaN(averageDurationChileautos)) {
    if (averageDurationChileautos < 30) {
      averageSpeedChileautos = 'Rápida';
      textAverageSpeedChileautos = '(menos de 30 días)';
    } else if (averageDurationChileautos <= 60) {
      averageSpeedChileautos = 'Media';
      textAverageSpeedChileautos = '(30-60 días)';
    } else {
      averageSpeedChileautos = 'Lenta';
      textAverageSpeedChileautos = '(más de 60 días)';
    }
  }

  const countDiscarded = publications.invalids?.length ?? 0;
  if (countPublications === 0 && countDiscarded === 0) {
    return <CardWithoutInfo haveSimilarVersions={haveSimilarVersions} haveYearPrices={haveYearPrices} />;
  }

  return (
    <Container>
      <PaddingContainer>
        <SubCont9>
          <CardPrimaryData title="Precio promedio en publicaciones:" value={`$${priceFormat(roundPrice(averagePublications, 1))}`} text="" />
          <CardPrimaryData title="Publicaciones encontradas:" value={`${countPublications}`} text="" />
          <CardPrimaryData title="Presencia en publicaciones:" value={`${publications.status}`} text={statusDescription[publications.status as keyof typeof statusDescription] || ''} />
          <CardPrimaryData title="Duración promedio publicaciones" value={`${publications.average_duration ? `${Math.round(publications.average_duration)} días` : 'Sin información'}`} text="Promedio publicaciones activas y finalizadas últimos 60 días" />
        </SubCont9>
      </PaddingContainer>
      <PaddingContainer>
        <BlankSpace height="10px" />
        <Text4B>Listado de publicaciones encontradas</Text4B>
        <BlankSpace height="0px" />
        <Text3>Accede al listado de publicaciones encontradas por portal.</Text3>
      </PaddingContainer>
      <BlankSpace height="10px" />
      {portals.map((portal) => {
        const pubs = getPublicationsByPortal(portal).slice().sort((a, b) => a.precio - b.precio);
        if (pubs.length === 0) return null;
        return (
          <PortalCard key={portal}>
            <Text4B style={{ marginBottom: '10px' }}>
              Publicaciones
              {' '}
              {publicationsTableSubtitle(portal)}
            </Text4B>
            {portal === DISCARDED_TAB && (
              <>
                <BlankSpace height="10px" />
                <Text4>
                  <Text4B>¿Qué son las publicaciones descartadas? </Text4B>
                  Algunas publicaciones presentan problemas e inconsistencias como precio formato inválido, precio muy fuera de rangos de mercado, duplicidad en un mismo portal, etc.
                  <br />
                  Este tipo de publicaciones no se consideran válidas para el cálculo de un precio justo de mercado, sin embargo disponibilizamos para completitud y transparencia de la información.
                </Text4>
              </>
            )}
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <Th><Text4B>Precio</Text4B></Th>
                    {portal !== 'Chileautos' && <Th><Text4B>Sitio o portal</Text4B></Th>}
                    {portal === 'Chileautos' && (
                      <>
                        <Th><Text4B>Publicado hace</Text4B></Th>
                        <Th><Text4B>Tipo vendedor</Text4B></Th>
                      </>
                    )}
                    {portal === DISCARDED_TAB && <Th><Text4B>Motivo descarte</Text4B></Th>}
                    <Th><Text4B>Link</Text4B></Th>
                  </tr>
                </thead>
                <tbody>
                  {pubs.map((pub, idx) => {
                    let dias = '-';
                    if (pub.fecha_est && createdAt) {
                      try {
                        const fechaEst = parseISO(pub.fecha_est);
                        const created = parseISO(createdAt);
                        dias = `${Math.abs(differenceInDays(created, fechaEst))} días`;
                      } catch {
                        dias = '-';
                      }
                    }

                    const url = `${b2bBaseUrl}/stock/valida_link?r=https://s3-sa-east-1.amazonaws.com/respaldo-fotos-extraccion/${pub.ss_path}&q=${pub.url_respaldo}`;

                    const rowBg = idx % 2 === 0 ? '#fff' : '#F6F6F6';
                    const rowKey = portal === DISCARDED_TAB
                      ? `discarded-${String(idx)}-${pub.url_respaldo ?? ''}-${pub.ss_path ?? ''}`
                      : pub.url_respaldo;
                    return (
                      <tr key={rowKey} style={{ background: rowBg }}>
                        <Td>
                          <Text4B>
                            $
                            {priceFormat(pub.precio)}
                          </Text4B>
                        </Td>
                        {portal !== 'Chileautos' && (
                          <Td>
                            <Text4>{pub.portal}</Text4>
                          </Td>
                        )}
                        {portal === 'Chileautos' && (
                          <>
                            <Td>
                              <Text4>{dias}</Text4>
                            </Td>
                            <Td>
                              <Text4>{pub.seller_type && pub.seller_type !== '0' ? capitalizeFirst(pub.seller_type) : 'No especificado'}</Text4>
                            </Td>
                          </>
                        )}
                        {portal === DISCARDED_TAB && (
                          <Td>
                            <Text4>
                              {pub.motivo_inv === undefined ? '-' : getInvalidReasonLabel(pub.motivo_inv)}
                            </Text4>
                          </Td>
                        )}
                        <Td>
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#0B3254', textDecoration: 'underline', fontSize: '14px' }}>Link publicación</a>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableContainer>
            {portal === 'Chileautos' && (
              <PaddingContainer>
                <ChileautosContainer>
                  <InfoChileautosAlliance>
                    <img src={chileautosLogo} width={41} height={15} alt="" />
                    <BlankSpace width="10px" />
                    <Text3>Información exclusiva gracias a la alianza entre Autofact y Chileautos.</Text3>
                  </InfoChileautosAlliance>
                  <BlankSpace height="10px" />
                  <SubCont9>
                    <ChileautosCard title="Precio promedio publicado:" value={`$${priceFormat(roundPrice(averagePriceChileautos, 1))}`} text="" />
                    <ChileautosCard title="Promedio tiempo de publicación:" value={`${averageDurationChileautos ? `${Math.round(averageDurationChileautos)} días` : '-'}`} text="" />
                    <ChileautosCard title="Velocidad promedio de venta:" value={averageSpeedChileautos} text={textAverageSpeedChileautos} />
                    <ChileautosCard title="Ranking mas publicados:" value={`${rankingChileautos.ranking ? `${rankingChileautos.ranking}/100` : 'Este vehículo no está en el top 100 de los más publicados'}`} text="(En base al modelo con hasta 10 años de antigüedad)" />
                  </SubCont9>
                </ChileautosContainer>
              </PaddingContainer>
            )}
          </PortalCard>
        );
      })}
    </Container>
  );
}

function capitalizeFirst(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function CardPrimaryData({ title, value, text }: Readonly<{title: string, value: string, text: string | null}>) {
  return (
    <ImportantSettingsContainer>
      <ImportantSettingsSubCont1>
        <SubCont4>
          <TitlePrice>{title}</TitlePrice>
          <Text5B>
            {value}
          </Text5B>
        </SubCont4>
      </ImportantSettingsSubCont1>
      { text ? (
        <ImportantSettingsSubCont2>
          <AutoIconText
            text={text}
            text2=""
            icon={infoImg}
          />
        </ImportantSettingsSubCont2>
      ) : ''}
    </ImportantSettingsContainer>
  );
}

function CardWithoutInfo({ haveSimilarVersions, haveYearPrices }: Readonly<{ haveSimilarVersions: boolean, haveYearPrices: boolean }>) {
  return (
    <Container>
      <SubCont4 style={{ alignItems: 'center', textAlign: 'center', marginTop: '20px' }}>
        <img src={autoFelizImg} width={48} height={48} alt="" />
        <BlankSpace width="10px" height="20px" />
        <Text4B style={{ color: '#0B3254', marginBottom: '10px' }}>
          Lo sentimos, no hemos encontrado publicaciones de venta activas para este modelo.
        </Text4B>
        {haveSimilarVersions && haveYearPrices ? (
          <Text4 style={{ color: '#3D5176', marginBottom: '30px' }}>
            Sin embargo puedes consultar en la siguiente sección precios para las versiones similares
            <br />
            o para la misma versión en diferentes años.
          </Text4>
        ) : ''}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '20px', marginBottom: '20px', background: '#EBF7FF', borderRadius: '10px', padding: '10px', border: '2px dashed #3DB2FF',
        }}
        >
          <span role="img" aria-label="info" style={{ fontSize: '22px', marginTop: '2px' }}>
            <img src={bellImg} width={16} height={16} alt="" />
          </span>
          <div style={{ textAlign: 'left' }}>
            <Text3B style={{ color: '#0B3254' }}>¿Por qué no fue posible encontrar publicaciones activas?</Text3B>
            <BlankSpace height="2px" />
            <Text3 style={{ color: '#0B3254' }}>
              Esto puede deberse a que el modelo consultado tiene muy poca presencia de mercado ya sea por ser un modelo y versión muy antiguo o de un segmento con baja presencia como de alta gama.
            </Text3>
          </div>
        </div>
      </SubCont4>
    </Container>
  );
}

function AutoIconText({ text, text2, icon }: Readonly<{ text: string | null, text2: string | null, icon: string }>) {
  return (
    <AutoIconTextContainer>
      <img src={icon} width={15} height={15} alt="" />
      <BlankSpace width="15px" />
      <Text2>
        {text}
        <BlankSpace width="2px" />
        {text2}
      </Text2>
    </AutoIconTextContainer>
  );
}

function ChileautosCard({ title, value, text }: Readonly<{title: string, value: string, text: string | null}>) {
  return (
    <ChileautosCardContainer>
      <Text3>{title}</Text3>
      <BlankSpace height="5px" />
      <Text4B>{value}</Text4B>
      <BlankSpace height="5px" />
      <Text3>{text}</Text3>
    </ChileautosCardContainer>
  );
}

const ChileautosCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  // justify-content: space-between;
  width: 100%;
`;

const ChileautosContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 98%;
  border: 1px solid #C5C5C5;
  border-radius: 10px;
  padding: 10px;
  @media (max-width: 700px){
    width: auto;
  }
`;

const InfoChileautosAlliance = styled.div`
  padding: 5px 5px;
  margin-top: 5px;
  border-radius: 5px;
  background: rgba(0, 124, 194, 0.15);
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const AutoIconTextContainer = styled.div`
  width: 100%;
  margin: 10px 0px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Container = styled.div`
  padding: 10px 0;
`;
const PaddingContainer = styled.div`
  padding: 0px 5px;
`;

const SubCont9 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
  @media (max-width: 700px){
    flex-direction: column;
    justify-content: center;
  }
`;

const ImportantSettingsContainer = styled.div`
  margin: 10px;
  border: 1px solid #C5C5C5;
  background: #F6F6F6;
  border-radius: 10px;
  padding: 10px;
  width: 100%;
  @media (max-width: 700px){
    width: auto;
  }
`;

const ImportantSettingsSubCont1 = styled.div`
  flex-direction: row;
  margin-bottom: 0px;
  margin-top: 0px;
  justify-content: center;
  padding: 10px 0;
`;

const PortalCard = styled.div`
  border: 1px solid #C5C5C5;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 30px;
`;

const SubCont4 = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: 700px){
    flex: 1;
  }
`;

const TitlePrice = styled.div`
  color: #0B3254;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const ImportantSettingsSubCont2 = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableContainer = styled.div`
  margin: 10px 0;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: #F6F6F6;
  padding: 15px;
  text-align: left;
  color: #0B3254;
  font-weight: 700;
`;

const Td = styled.td`
  padding: 15px;
`;
